import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PromoCodeManager = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('codes');
  const token = localStorage.getItem('sessionToken');

  // User search for linking influencer
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [linkedUser, setLinkedUser] = useState(null);

  // Payout tracking
  const [payouts, setPayouts] = useState([]);
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutForm, setPayoutForm] = useState({
    influencer_user_id: '', influencer_name: '', amount: '', payment_method: 'bank_transfer',
    reference_number: '', notes: '', promo_code_ids: []
  });

  const [form, setForm] = useState({
    code: '', discount_type: 'percentage', discount_value: 10,
    influencer_name: '', influencer_contact: '', influencer_user_id: '', influencer_payout_percent: 5,
    max_total_uses: '', max_uses_per_user: '1', min_booking_amount: '',
    expires_at: '', is_active: true
  });

  const fetchCodes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/promo-codes`, { headers: { Authorization: `Bearer ${token}` } });
      setCodes(res.data.promo_codes || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/promo-codes/analytics`, { headers: { Authorization: `Bearer ${token}` } });
      setAnalytics(res.data);
    } catch (e) { console.error(e); }
  }, [token]);

  useEffect(() => { fetchCodes(); fetchAnalytics(); }, [fetchCodes, fetchAnalytics]);

  // Search users for influencer linking
  useEffect(() => {
    if (userSearch.length < 2) { setUserResults([]); return; }
    const timer = setTimeout(async () => {
      setSearchingUsers(true);
      try {
        const res = await axios.get(`${API}/api/admin/users/search-for-link?q=${encodeURIComponent(userSearch)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserResults(res.data.users || []);
      } catch (e) { setUserResults([]); }
      setSearchingUsers(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearch, token]);

  const selectLinkedUser = (user) => {
    setLinkedUser(user);
    setForm(f => ({ ...f, influencer_user_id: user.id, influencer_name: f.influencer_name || user.name }));
    setUserSearch('');
    setUserResults([]);
  };

  const removeLinkedUser = () => {
    setLinkedUser(null);
    setForm(f => ({ ...f, influencer_user_id: '' }));
  };

  const fetchPayouts = useCallback(async () => {
    setPayoutsLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/influencer-payouts`, { headers: { Authorization: `Bearer ${token}` } });
      setPayouts(res.data.payouts || []);
    } catch (e) { console.error('Failed to load payouts:', e); }
    setPayoutsLoading(false);
  }, [token]);

  useEffect(() => { if (activeTab === 'payouts') fetchPayouts(); }, [activeTab, fetchPayouts]);

  const openPayoutForInfluencer = (promo) => {
    setPayoutForm({
      influencer_user_id: promo.influencer_user_id || '',
      influencer_name: promo.influencer_name || '',
      amount: (promo.total_influencer_earnings || 0).toFixed(2),
      payment_method: 'bank_transfer',
      reference_number: '',
      notes: `Payout for code: ${promo.code}`,
      promo_code_ids: [promo.id]
    });
    setShowPayoutModal(true);
  };

  const handleRecordPayout = async () => {
    if (!payoutForm.influencer_user_id || !payoutForm.amount || parseFloat(payoutForm.amount) <= 0) {
      alert('Please fill in the influencer and a valid amount');
      return;
    }
    try {
      await axios.post(`${API}/api/admin/influencer-payouts`, {
        ...payoutForm,
        amount: parseFloat(payoutForm.amount)
      }, { headers: { Authorization: `Bearer ${token}` } });
      setShowPayoutModal(false);
      setPayoutForm({ influencer_user_id: '', influencer_name: '', amount: '', payment_method: 'bank_transfer', reference_number: '', notes: '', promo_code_ids: [] });
      if (activeTab === 'payouts') fetchPayouts();
      alert('Payout recorded successfully!');
    } catch (e) {
      alert('Failed to record payout: ' + (e.response?.data?.detail || e.message));
    }
  };

  const generateCode = async () => {
    try {
      const prefix = form.influencer_name ? form.influencer_name.replace(/\s+/g, '').slice(0, 5).toUpperCase() : '';
      const res = await axios.post(`${API}/api/admin/promo-codes/generate`,
        { prefix, length: 6 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setForm({ ...form, code: res.data.code });
    } catch (e) { alert('Failed to generate code'); }
  };

  const handleCreate = async () => {
    if (!form.discount_value) { alert('Discount value is required'); return; }
    try {
      const payload = { ...form };
      if (!payload.code) {
        const prefix = payload.influencer_name ? payload.influencer_name.replace(/\s+/g, '').slice(0, 5).toUpperCase() : '';
        payload.prefix = prefix;
      }
      ['max_total_uses', 'max_uses_per_user', 'min_booking_amount'].forEach(k => {
        if (payload[k] === '' || payload[k] === null) payload[k] = null;
        else payload[k] = Number(payload[k]);
      });
      payload.discount_value = Number(payload.discount_value);
      payload.influencer_payout_percent = Number(payload.influencer_payout_percent);

      await axios.post(`${API}/api/admin/promo-codes`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setShowCreate(false);
      resetForm();
      fetchCodes();
      fetchAnalytics();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to create'); }
  };

  const handleUpdate = async () => {
    if (!editingCode) return;
    try {
      const payload = { ...form };
      ['max_total_uses', 'max_uses_per_user', 'min_booking_amount'].forEach(k => {
        if (payload[k] === '' || payload[k] === null) payload[k] = null;
        else payload[k] = Number(payload[k]);
      });
      payload.discount_value = Number(payload.discount_value);
      payload.influencer_payout_percent = Number(payload.influencer_payout_percent);

      await axios.put(`${API}/api/admin/promo-codes/${editingCode}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setEditingCode(null);
      setShowCreate(false);
      resetForm();
      fetchCodes();
    } catch (e) { alert(e.response?.data?.detail || 'Failed to update'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this promo code?')) return;
    try {
      await axios.delete(`${API}/api/admin/promo-codes/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchCodes();
      fetchAnalytics();
    } catch (e) { alert('Failed to delete'); }
  };

  const handleToggleActive = async (promo) => {
    try {
      await axios.put(`${API}/api/admin/promo-codes/${promo.id}`,
        { is_active: !promo.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchCodes();
    } catch (e) { alert('Failed to toggle'); }
  };

  const startEdit = (promo) => {
    setEditingCode(promo.id);
    setForm({
      code: promo.code, discount_type: promo.discount_type, discount_value: promo.discount_value,
      influencer_name: promo.influencer_name || '', influencer_contact: promo.influencer_contact || '',
      influencer_user_id: promo.influencer_user_id || '',
      influencer_payout_percent: promo.influencer_payout_percent || 0,
      max_total_uses: promo.max_total_uses ?? '', max_uses_per_user: promo.max_uses_per_user ?? '',
      min_booking_amount: promo.min_booking_amount ?? '', expires_at: promo.expires_at || '', is_active: promo.is_active
    });
    if (promo.influencer_user_id) {
      setLinkedUser({ id: promo.influencer_user_id, name: promo.influencer_name || '', email: '' });
    } else {
      setLinkedUser(null);
    }
    setShowCreate(true);
  };

  const resetForm = () => {
    setForm({
      code: '', discount_type: 'percentage', discount_value: 10,
      influencer_name: '', influencer_contact: '', influencer_user_id: '', influencer_payout_percent: 5,
      max_total_uses: '', max_uses_per_user: '1', min_booking_amount: '',
      expires_at: '', is_active: true
    });
    setLinkedUser(null);
    setUserSearch('');
    setUserResults([]);
  };

  const summary = analytics?.summary || {};

  return (
    <div className="min-h-screen bg-gray-50" data-testid="promo-code-manager">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold">Promo Codes & Influencer Marketing</h1>
          <p className="text-purple-100 mt-2">Manage discount codes, track influencer performance</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {[
            { label: 'Active Codes', value: summary.active_codes || 0, color: 'text-green-600' },
            { label: 'Total Uses', value: summary.total_uses || 0, color: 'text-blue-600' },
            { label: 'Revenue Driven', value: `${(summary.total_revenue_driven || 0).toFixed(2)} KD`, color: 'text-purple-600' },
            { label: 'Discounts Given', value: `${(summary.total_discount_given || 0).toFixed(2)} KD`, color: 'text-red-600' },
            { label: 'Influencer Payouts', value: `${(summary.total_influencer_payouts || 0).toFixed(2)} KD`, color: 'text-amber-600' },
            { label: 'Net After Discounts', value: `${((summary.total_revenue_driven || 0) - (summary.total_discount_given || 0)).toFixed(2)} KD`, color: 'text-green-600' },
          ].map((s, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-xs text-gray-500 mb-1">{s.label}</p>
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {['codes', 'analytics', 'payouts'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab ? (tab === 'payouts' ? 'bg-amber-600 text-white' : 'bg-purple-600 text-white') : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
              data-testid={`${tab}-tab-btn`}
            >{tab === 'codes' ? 'Manage Codes' : tab === 'analytics' ? 'Analytics' : 'Payouts'}</button>
          ))}
          <button onClick={() => { resetForm(); setEditingCode(null); setShowCreate(true); }}
            className="ml-auto px-5 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700" data-testid="create-promo-btn">
            + Create Code
          </button>
        </div>

        {/* Create/Edit Form Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-semibold">{editingCode ? 'Edit Promo Code' : 'Create Promo Code'}</h3>
                <button onClick={() => { setShowCreate(false); setEditingCode(null); resetForm(); }} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="p-6 space-y-4">
                {/* Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Promo Code</label>
                  <div className="flex gap-2">
                    <input type="text" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm uppercase" placeholder="Auto-generated if blank"
                      disabled={!!editingCode} data-testid="promo-code-input" />
                    {!editingCode && (
                      <button onClick={generateCode} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 border border-gray-300"
                        data-testid="generate-code-btn">Generate</button>
                    )}
                  </div>
                </div>

                {/* Discount */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Discount Type</label>
                    <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount (KD)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Discount Value {form.discount_type === 'percentage' ? '(%)' : '(KD)'}
                    </label>
                    <input type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" min="0" step="0.5" data-testid="discount-value-input" />
                  </div>
                </div>

                {/* Influencer */}
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                  <h4 className="text-sm font-semibold text-purple-800 mb-3">Influencer Details</h4>
                  
                  {/* Link User Account */}
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Link User Account</label>
                    {linkedUser ? (
                      <div className="flex items-center gap-2 bg-white px-3 py-2 border border-purple-300 rounded-lg" data-testid="linked-user-display">
                        <div className="w-6 h-6 bg-purple-200 rounded-full flex items-center justify-center text-xs font-bold text-purple-700">
                          {(linkedUser.name || linkedUser.email || '?')[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{linkedUser.name || linkedUser.email}</p>
                          {linkedUser.email && <p className="text-xs text-gray-500 truncate">{linkedUser.email}</p>}
                        </div>
                        <button onClick={removeLinkedUser} className="text-red-500 hover:text-red-700 text-xs px-2 py-1 border border-red-200 rounded hover:bg-red-50"
                          data-testid="unlink-user-btn">Unlink</button>
                      </div>
                    ) : (
                      <div className="relative">
                        <input type="text" value={userSearch} onChange={(e) => setUserSearch(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Search by name or email..."
                          data-testid="user-search-input" />
                        {searchingUsers && <div className="absolute right-3 top-2.5 text-xs text-gray-400">Searching...</div>}
                        {userResults.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                            {userResults.map(u => (
                              <button key={u.id} onClick={() => selectLinkedUser(u)}
                                className="w-full text-left px-3 py-2 hover:bg-purple-50 text-sm flex items-center gap-2 border-b border-gray-100 last:border-0"
                                data-testid={`user-result-${u.id}`}>
                                <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs font-bold text-gray-600">
                                  {(u.name || u.email || '?')[0].toUpperCase()}
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">{u.name}</p>
                                  <p className="text-xs text-gray-500">{u.email}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-purple-600 mt-1">Link a user account to give them access to the Influencer Dashboard</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                      <input type="text" value={form.influencer_name} onChange={(e) => setForm({ ...form, influencer_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Influencer name" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Contact</label>
                      <input type="text" value={form.influencer_contact} onChange={(e) => setForm({ ...form, influencer_contact: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="@handle or phone" />
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Influencer Payout (% of booking fee)</label>
                    <input type="number" value={form.influencer_payout_percent} onChange={(e) => setForm({ ...form, influencer_payout_percent: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" min="0" max="50" step="0.5" />
                  </div>
                </div>

                {/* Limits */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Max Total Uses</label>
                    <input type="number" value={form.max_total_uses} onChange={(e) => setForm({ ...form, max_total_uses: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Unlimited" min="0" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Max Per User</label>
                    <input type="number" value={form.max_uses_per_user} onChange={(e) => setForm({ ...form, max_uses_per_user: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Unlimited" min="0" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Min Booking (KD)</label>
                    <input type="number" value={form.min_booking_amount} onChange={(e) => setForm({ ...form, min_booking_amount: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="None" min="0" step="0.5" />
                  </div>
                </div>

                {/* Expiry */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                  <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="promo-expiry-input" />
                </div>

                {/* Active toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Active</span>
                  <button onClick={() => setForm({ ...form, is_active: !form.is_active })}
                    className={`w-12 h-6 rounded-full transition-colors relative ${form.is_active ? 'bg-green-500' : 'bg-gray-300'}`}>
                    <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.is_active ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>

                <div className="flex gap-3 pt-2">
                  <button onClick={editingCode ? handleUpdate : handleCreate}
                    className="flex-1 bg-purple-600 text-white py-2.5 rounded-lg font-medium hover:bg-purple-700 transition-colors"
                    data-testid="save-promo-btn">
                    {editingCode ? 'Update Code' : 'Create Code'}
                  </button>
                  <button onClick={() => { setShowCreate(false); setEditingCode(null); resetForm(); }}
                    className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg border border-gray-200">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Codes Tab */}
        {activeTab === 'codes' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Code</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Discount</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Influencer</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600">Uses</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600">Revenue</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600">Inf. Earnings</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600">Status</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr><td colSpan={8} className="text-center py-8 text-gray-400">Loading...</td></tr>
                  ) : codes.length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-8 text-gray-400">No promo codes yet. Create one!</td></tr>
                  ) : codes.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50" data-testid={`promo-row-${p.id}`}>
                      <td className="px-4 py-3">
                        <span className="font-mono font-bold text-purple-700 bg-purple-50 px-2 py-1 rounded">{p.code}</span>
                        {p.expires_at && (
                          <div className="text-xs text-gray-400 mt-1">
                            Exp: {p.expires_at}
                            {p.expires_at < new Date().toISOString().split('T')[0] && (
                              <span className="text-red-500 ml-1">(expired)</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-semibold">{p.discount_value}{p.discount_type === 'percentage' ? '%' : ' KD'}</span>
                        <span className="text-gray-400 text-xs ml-1">{p.discount_type}</span>
                      </td>
                      <td className="px-4 py-3">
                        {p.influencer_name ? (
                          <div>
                            <p className="font-medium text-gray-900">{p.influencer_name}</p>
                            <p className="text-xs text-gray-500">{p.influencer_payout_percent}% payout</p>
                          </div>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="text-center px-4 py-3">
                        <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium">
                          {p.total_uses || 0}{p.max_total_uses ? `/${p.max_total_uses}` : ''}
                        </span>
                      </td>
                      <td className="text-right px-4 py-3 font-medium">{(p.total_booking_revenue || 0).toFixed(2)} KD</td>
                      <td className="text-right px-4 py-3 text-amber-600 font-medium">{(p.total_influencer_earnings || 0).toFixed(2)} KD</td>
                      <td className="text-center px-4 py-3">
                        <button onClick={() => handleToggleActive(p)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            p.is_active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                          }`}>
                          {p.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="text-right px-4 py-3">
                        <div className="flex justify-end gap-2">
                          {p.influencer_user_id && (
                            <button onClick={() => openPayoutForInfluencer(p)}
                              className="text-amber-600 hover:text-amber-800 text-xs font-medium" data-testid={`payout-btn-${p.id}`}>Payout</button>
                          )}
                          <button onClick={() => startEdit(p)} className="text-blue-600 hover:text-blue-800 text-xs font-medium">Edit</button>
                          <button onClick={() => handleDelete(p.id)} className="text-red-600 hover:text-red-800 text-xs font-medium">Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && analytics && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">Influencer Performance</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Influencer</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Code</th>
                      <th className="text-center px-4 py-3 font-semibold text-gray-600">Uses</th>
                      <th className="text-right px-4 py-3 font-semibold text-gray-600">Revenue Driven</th>
                      <th className="text-right px-4 py-3 font-semibold text-gray-600">Discounts Given</th>
                      <th className="text-right px-4 py-3 font-semibold text-gray-600">Payout Owed</th>
                      <th className="text-right px-4 py-3 font-semibold text-gray-600">ROI</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(analytics.promo_codes || []).filter(p => p.influencer_name).map(p => {
                      const cost = (p.total_discount_given || 0) + (p.total_influencer_earnings || 0);
                      const roi = cost > 0 ? (((p.total_booking_revenue || 0) - cost) / cost * 100).toFixed(0) : '—';
                      return (
                        <tr key={p.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900">{p.influencer_name}</p>
                            <p className="text-xs text-gray-500">{p.influencer_contact}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-mono text-purple-700 bg-purple-50 px-2 py-0.5 rounded text-xs">{p.code}</span>
                          </td>
                          <td className="text-center px-4 py-3">{p.total_uses || 0}</td>
                          <td className="text-right px-4 py-3 font-medium">{(p.total_booking_revenue || 0).toFixed(2)} KD</td>
                          <td className="text-right px-4 py-3 text-red-600">{(p.total_discount_given || 0).toFixed(2)} KD</td>
                          <td className="text-right px-4 py-3 text-amber-600 font-bold">{(p.total_influencer_earnings || 0).toFixed(2)} KD</td>
                          <td className="text-right px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              roi !== '—' && Number(roi) > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                            }`}>{roi}{roi !== '—' ? '%' : ''}</span>
                          </td>
                        </tr>
                      );
                    })}
                    {!(analytics.promo_codes || []).some(p => p.influencer_name) && (
                      <tr><td colSpan={7} className="text-center py-8 text-gray-400">No influencer codes yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Recent Usage */}
            {(analytics.recent_usage || []).length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-900">Recent Promo Code Usage</h3>
                </div>
                <div className="divide-y divide-gray-100">
                  {(analytics.recent_usage || []).map((u, i) => (
                    <div key={i} className="flex items-center justify-between px-6 py-3 text-sm">
                      <div>
                        <span className="font-mono text-purple-700 bg-purple-50 px-2 py-0.5 rounded text-xs mr-2">{u.code}</span>
                        <span className="text-gray-600">Booking: {u.booking_amount} KD</span>
                      </div>
                      <div className="flex gap-4 text-xs">
                        <span className="text-red-600">-{u.discount_amount} KD discount</span>
                        {u.influencer_earning > 0 && <span className="text-amber-600">{u.influencer_earning} KD inf. earning</span>}
                        <span className="text-gray-400">{new Date(u.used_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Payouts Tab */}
      {activeTab === 'payouts' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Influencer Payout Records</h3>
            <button onClick={() => { setPayoutForm({ influencer_user_id: '', influencer_name: '', amount: '', payment_method: 'bank_transfer', reference_number: '', notes: '', promo_code_ids: [] }); setShowPayoutModal(true); }}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 flex items-center gap-2"
              data-testid="record-payout-btn">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Record Payout
            </button>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Date</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Influencer</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600">Amount</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Method</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Reference</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Notes</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {payoutsLoading ? (
                    <tr><td colSpan={7} className="text-center py-8 text-gray-400">Loading...</td></tr>
                  ) : payouts.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-8 text-gray-400">No payouts recorded yet</td></tr>
                  ) : payouts.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50" data-testid={`payout-row-${p.id}`}>
                      <td className="px-4 py-3 text-gray-500">{new Date(p.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{p.influencer_name || 'N/A'}</td>
                      <td className="text-right px-4 py-3 text-amber-700 font-bold">{p.amount.toFixed(2)} KD</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                          {p.payment_method === 'bank_transfer' ? 'Bank Transfer' : p.payment_method === 'cash' ? 'Cash' : p.payment_method === 'wallet' ? 'Wallet' : p.payment_method}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs font-mono">{p.reference_number || '—'}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs max-w-[200px] truncate">{p.notes || '—'}</td>
                      <td className="text-center px-4 py-3">
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          {p.status === 'completed' ? 'Paid' : p.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {payouts.length > 0 && (
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-between items-center text-sm">
                <span className="text-gray-500">{payouts.length} payout(s)</span>
                <span className="font-bold text-amber-700">Total Paid: {payouts.reduce((s, p) => s + p.amount, 0).toFixed(2)} KD</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Record Payout Modal */}
      {showPayoutModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" data-testid="payout-modal">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Record Influencer Payout
            </h3>

            <div className="space-y-4">
              {/* Influencer Selection */}
              {payoutForm.influencer_name ? (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Influencer</label>
                  <div className="px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg text-sm font-medium text-purple-700">
                    {payoutForm.influencer_name}
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Select Influencer</label>
                  <select value={payoutForm.influencer_user_id}
                    onChange={(e) => {
                      const selected = codes.find(c => c.influencer_user_id === e.target.value);
                      setPayoutForm(f => ({ ...f, influencer_user_id: e.target.value, influencer_name: selected?.influencer_name || '' }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="payout-influencer-select">
                    <option value="">Choose an influencer...</option>
                    {[...new Map(codes.filter(c => c.influencer_user_id).map(c => [c.influencer_user_id, c])).values()].map(c => (
                      <option key={c.influencer_user_id} value={c.influencer_user_id}>{c.influencer_name} ({c.code})</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Amount (KD)</label>
                <input type="number" value={payoutForm.amount} onChange={(e) => setPayoutForm(f => ({ ...f, amount: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="0.00" min="0" step="0.01"
                  data-testid="payout-amount-input" />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Payment Method</label>
                <select value={payoutForm.payment_method} onChange={(e) => setPayoutForm(f => ({ ...f, payment_method: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" data-testid="payout-method-select">
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cash">Cash</option>
                  <option value="wallet">Wallet</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Reference Number (optional)</label>
                <input type="text" value={payoutForm.reference_number} onChange={(e) => setPayoutForm(f => ({ ...f, reference_number: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Transaction ID or ref #" />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes (optional)</label>
                <textarea value={payoutForm.notes} onChange={(e) => setPayoutForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows="2" placeholder="Payment notes..." />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowPayoutModal(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">Cancel</button>
              <button onClick={handleRecordPayout}
                className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700"
                data-testid="confirm-payout-btn">Record Payout</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoCodeManager;
